// useUsername Hook
// Manages username storage, validation, and authentication with avatar support.
//
// Auth state is shared app-wide via UsernameProvider/context, so logging in
// anywhere (modal, /login page) instantly updates the whole UI — the sidebar,
// header, profile, etc. — instead of each component tracking login separately.

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  getSavedAuth,
  renameAccount,
  verifySavedAuth,
  registerUser,
  loginUser,
  clearAuth,
  checkUsernameExists,
  validatePassword,
  updateUserAvatar,
} from '../services/authService';
import { DEFAULT_AVATAR } from '../config/avatarConfig';
import { trackEvent, trackUserRegistration, trackUserLogin, setSignedIn, tagPlayer } from '../services/analyticsService';

// Validation rules for username
const MIN_LENGTH = 3;
const MAX_LENGTH = 15;
const VALID_PATTERN = /^[a-zA-Z0-9_]+$/;

export const validateUsername = (username) => {
  if (!username || username.trim() === '') {
    return { valid: false, error: 'Please enter a pirate name!' };
  }

  const trimmed = username.trim();

  if (trimmed.length < MIN_LENGTH) {
    return { valid: false, error: `Username must be at least ${MIN_LENGTH} characters` };
  }

  if (trimmed.length > MAX_LENGTH) {
    return { valid: false, error: `Username must be ${MAX_LENGTH} characters or less` };
  }

  if (!VALID_PATTERN.test(trimmed)) {
    return { valid: false, error: 'Only letters, numbers, and underscores allowed' };
  }

  return { valid: true, error: null };
};

export { validatePassword };

// Internal: builds the auth state. Used ONCE by UsernameProvider below.
const useProvideUsername = () => {
  const [username, setUsernameState] = useState(null);
  const [avatar, setAvatarState] = useState(DEFAULT_AVATAR);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load and verify saved auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const savedAuth = getSavedAuth();

      if (savedAuth) {
        // Verify the saved credentials are still valid
        const verification = await verifySavedAuth();

        if (verification.valid) {
          setUsernameState(verification.username);
          setAvatarState(verification.avatar || DEFAULT_AVATAR);
          setIsAuthenticated(true);
          setSignedIn(true);
          tagPlayer(verification.username);
          try {
            if (!sessionStorage.getItem('opq-visit-counted')) {
              sessionStorage.setItem('opq-visit-counted', '1');
              trackEvent('signed_in_visit', { event_category: 'User' });
            }
          } catch (e) { /* private mode */ }
        } else if (verification.rejected) {
          // The server said this password is wrong (it was reset, or the
          // account is gone): only then forget it.
          clearAuth();
        } else {
          // No answer (offline, server down). Most players never saw their
          // password — quick save made it — so wiping it here lost their
          // account for good. Keep it and stay signed in as saved.
          setUsernameState(savedAuth.username);
          setAvatarState(savedAuth.avatar || DEFAULT_AVATAR);
          setIsAuthenticated(true);
        }
      }

      if (!savedAuth) { setSignedIn(false); tagPlayer(null); }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  // Register a new user with avatar
  const register = useCallback(async (newUsername, password, selectedAvatar = DEFAULT_AVATAR, email = '') => {
    // Validate username
    const usernameValidation = validateUsername(newUsername);
    if (!usernameValidation.valid) {
      return { success: false, error: usernameValidation.error };
    }

    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      return { success: false, error: passwordValidation.error };
    }

    // Register with auth service
    const result = await registerUser(newUsername.trim(), password, selectedAvatar, email);

    if (result.success) {
      setUsernameState(result.username);
      setAvatarState(result.avatar || DEFAULT_AVATAR);
      setIsAuthenticated(true);
      // Quick save makes a 36-character password nobody types; a chosen one is short.
      trackUserRegistration(password.length >= 30 ? 'quick_save' : 'password');
      setSignedIn(true);
      tagPlayer(result.username);
    }

    return result;
  }, []);

  // Login existing user
  const login = useCallback(async (existingUsername, password) => {
    const result = await loginUser(existingUsername, password);

    if (result.success) {
      setUsernameState(result.username);
      setAvatarState(result.avatar || DEFAULT_AVATAR);
      setIsAuthenticated(true);
      trackUserLogin('password');
      setSignedIn(true);
      tagPlayer(result.username);
    } else if (result.rejected) {
      trackEvent('login_failed', { reason: result.error === 'Incorrect password' ? 'wrong_password' : 'unknown_name', event_category: 'User' });
    }

    return result;
  }, []);

  // Logout user
  const logout = useCallback(() => {
    trackEvent('logout', { event_category: 'User' });
    setSignedIn(false);
    tagPlayer(null);
    clearAuth();
    setUsernameState(null);
    setAvatarState(DEFAULT_AVATAR);
    setIsAuthenticated(false);
  }, []);

  // Rename: the same account under a new name (nothing is lost)
  const rename = useCallback(async (newName) => {
    const result = await renameAccount(newName);
    if (result.success) {
      setUsernameState(result.username);
      trackEvent('username_change', { event_category: 'User' });
      tagPlayer(result.username);
    }
    return result;
  }, []);

  // Update avatar
  const updateAvatar = useCallback(async (newAvatar) => {
    if (!username) {
      return { success: false, error: 'Not logged in' };
    }

    const result = await updateUserAvatar(username, newAvatar);

    if (result.success) {
      setAvatarState(result.avatar);
      trackEvent('avatar_change', { kind: String(result.avatar).startsWith('http') ? 'character' : 'icon', event_category: 'User' });
    }

    return result;
  }, [username]);

  // Check if username is available (for registration)
  const checkAvailability = useCallback(async (usernameToCheck) => {
    try {
      const exists = await checkUsernameExists(usernameToCheck);
      return { available: !exists, error: exists ? 'Username is already taken' : null };
    } catch (err) {
      return { available: false, error: 'Could not verify username. Please try again.' };
    }
  }, []);

  // Legacy setUsername function (for backward compatibility)
  // This should NOT be used for new registrations - use register() instead
  const setUsername = useCallback((newUsername) => {
    const validation = validateUsername(newUsername);
    if (validation.valid) {
      setUsernameState(newUsername.trim());
      return { success: true };
    }
    return { success: false, error: validation.error };
  }, []);

  // Clear username (legacy - use logout instead)
  const clearUsername = useCallback(() => {
    logout();
  }, [logout]);

  // Check if username exists
  const hasUsername = Boolean(username);

  return {
    username,
    avatar,
    setUsername, // Legacy - for backward compatibility
    clearUsername, // Legacy - use logout instead
    hasUsername,
    isLoading,
    isAuthenticated,
    // New auth functions
    register,
    login,
    logout,
    updateAvatar,
    rename,
    checkAvailability,
  };
};

// Shared auth context — one source of truth for the whole app.
const UsernameContext = createContext(null);

export const UsernameProvider = ({ children }) => {
  const value = useProvideUsername();
  return (
    <UsernameContext.Provider value={value}>
      {children}
    </UsernameContext.Provider>
  );
};

// Components read the shared auth state through this hook.
export const useUsername = () => {
  const ctx = useContext(UsernameContext);
  if (ctx === null) {
    throw new Error('useUsername must be used within a UsernameProvider');
  }
  return ctx;
};

export default useUsername;
